import Utils from "./Customizations"

type SearchParams = {
  q: string;
  start?: number;
  rows?: number;
  sort?: string;
  limit?: number;
};

/**
 * Represents a single document found in the search results.
 */
type ResponseDocument = {
  documentKind: string;
  lastModifiedDate: string; // Represents an ISO date string
  last_publish_action: string;
  description?: string; // Optional field, as not all documents have it
  abstract?: string // Same as description field but need to map description to abstract for the UI
  title: string;
  view_uri: string;
  score: number;
}

/**
 * Represents the main data part of the response containing search results.
 */
type ResponseData = {
  docs: ResponseDocument[];
  numFound: number;
  start: number;
  maxScore: number;
  numFoundExact: boolean;
}

/**
 * Represents the entire content under the "pages" key.
 */
type PagesObject = {
  response: ResponseData;
}

/**
 * Represents the overall structure of the API response.
 */
type ApiResponse = {
  pages: PagesObject;
}

const defaultSearchParams: SearchParams = {
  q: '',
  start: 0,
  rows: 10,
};

/**
 * Performs a search operation using the WorkYourWay API.
 * 
 * @param searchParams - The search parameters to be used in the search query.
 * @returns A promise that resolves to the search results.
 */
export async function wywSearch(searchParams: SearchParams) {
  try {
    const viewUriBaseURL: URL = new URL(Utils.getViewUriOrigin());
    const url: URL = new URL('https://workyourway.redhat.com/bin/workyourway/search');
    const params: Record<string, any> = new URLSearchParams();

    const mergedSearchParams: SearchParams = {
      ...defaultSearchParams,
      ...searchParams,
    };

    /*
      * The Solr Fusion API is expecting "sortBy" instead
      * of "sort" for sorting results. We need to map "sort"
      * to "sortBy", etc
      */
    const paramMapping: Record<string, string> = {
      rows: 'limit',
    };

    Object.entries(mergedSearchParams).forEach(([key, value]) => {
      const newKey = paramMapping[key] || key;
      params.set(newKey, String(value));
    });

    url.search = params.toString();
    
    const response: Response = await fetch(url);
    console.log('WYW Search Response:', response);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: ApiResponse = await response.json();

    // need to map the description field to abstract for the display
    // and add the viewUriBaseURL to the beginning of the view_uri
    // property
    const transformedDocs = data.pages.response.docs.map(doc => ({
      ...doc,
      abstract: doc.description,
      view_uri: `${doc.view_uri}.html`,
    }));

    return {
      ...data.pages,
      response: {
        ...data.pages.response,
        docs: transformedDocs,
      },
    };
  } catch (error) {
    console.error('Error in wywSearch:', error);
    throw error;
  }
}